import { api } from '../lib/api';
import type { Backlink, EntityDocument, EntityFieldHistoryEntry, EntityTag, EntityTemplate, ResolvedEntityFieldValues } from '../types/entities';
import type { CharacterHistoryEntry } from '../types/relationships';
import type { CharacterHistoryTimelineOptions } from '../types/timeline';
import type { SceneConflict, SceneSegment, SceneSegmentField } from '../types/scenes';
import type { WorkspaceSummary } from '../types/workspace';

export type EntityTabCache = {
  contextKey: string;
  entity: EntityDocument;
  activeEntityTags: EntityTag[];
  characterHistory: CharacterHistoryEntry[];
  characterHistoryTimelineOptions: CharacterHistoryTimelineOptions;
  characterHistoryLoaded: boolean;
  entityFieldHistory: EntityFieldHistoryEntry[];
  resolvedEntityFieldValues: ResolvedEntityFieldValues | null;
  backlinks: Backlink[];
  sceneSegments: SceneSegment[];
  sceneSegmentFields: SceneSegmentField[];
  sceneConflicts: SceneConflict[];
};

type WorkspaceEntityCacheStore = {
  workspace: WorkspaceSummary | null;
  activeEntity: EntityDocument | null;
  activeEntityTags: EntityTag[];
  characterHistory: CharacterHistoryEntry[];
  characterHistoryTimelineOptions: CharacterHistoryTimelineOptions;
  characterHistoryLoaded: boolean;
  entityFieldHistory: EntityFieldHistoryEntry[];
  resolvedEntityFieldValues: ResolvedEntityFieldValues | null;
  backlinks: Backlink[];
  sceneSegments: SceneSegment[];
  sceneSegmentFields: SceneSegmentField[];
  sceneConflicts: SceneConflict[];
  entityTabCache: Map<string, EntityTabCache>;
  invalidateEntityOpenRequests(): void;
  globalTimeContext: { sceneId: string | null; segmentId: string | null };
  saveState: 'clean' | 'dirty' | 'saving' | 'saved' | 'conflict' | 'offline';
  templates: EntityTemplate[];
  loadResolvedEntityFieldValues(entityId?: string): Promise<void>;
  loadEntityFieldHistory(entityId?: string): Promise<void>;
  loadEntityTags(entityId?: string): Promise<void>;
  loadBacklinks(entityId?: string): Promise<void>;
  loadSceneSegments(sceneId?: string, entityVersionId?: string | null, entityRevisionId?: string | null): Promise<void>;
  loadSceneConflicts(sceneId?: string): Promise<void>;
  entityCacheKey(id: string): string;
  entityCacheContextKey(): string;
  cacheActiveEntity(): void;
  restoreCachedEntity(id: string): EntityDocument | null;
  validateCachedEntity(entity: EntityDocument, generation?: number): Promise<void>;
  openEntity(id: string, updateHistory?: boolean, force?: boolean, requireClean?: boolean): Promise<EntityDocument | null | undefined>;
};

const entityCacheLimit = 12;
const entityOpenGenerations = new WeakMap<object, number>();

function entityOpenGeneration(store: object) {
  return entityOpenGenerations.get(store) ?? 0;
}

export function invalidateEntityOpenRequests(store: object) {
  entityOpenGenerations.set(store, entityOpenGeneration(store) + 1);
}

export const workspaceEntityCacheActions = {
  invalidateEntityOpenRequests(this: WorkspaceEntityCacheStore) { invalidateEntityOpenRequests(this); },
  entityCacheKey(this: WorkspaceEntityCacheStore, id: string) { return `${this.workspace?.id ?? 'none'}:${id}`; },
  entityCacheContextKey(this: WorkspaceEntityCacheStore) { return `${this.globalTimeContext.sceneId ?? ''}:${this.globalTimeContext.segmentId ?? ''}`; },
  cacheActiveEntity(this: WorkspaceEntityCacheStore) {
    const entity = this.activeEntity;
    if (!entity?.changeToken || !this.workspace) return;
    const key = this.entityCacheKey(entity.id);
    this.entityTabCache.delete(key);
    this.entityTabCache.set(key, {
      contextKey: this.entityCacheContextKey(), entity,
      activeEntityTags: this.activeEntityTags, characterHistory: this.characterHistory,
      characterHistoryTimelineOptions: this.characterHistoryTimelineOptions,
      characterHistoryLoaded: this.characterHistoryLoaded,
      entityFieldHistory: this.entityFieldHistory, resolvedEntityFieldValues: this.resolvedEntityFieldValues,
      backlinks: this.backlinks, sceneSegments: this.sceneSegments,
      sceneSegmentFields: this.sceneSegmentFields, sceneConflicts: this.sceneConflicts
    });
    while (this.entityTabCache.size > entityCacheLimit) {
      const oldest = this.entityTabCache.keys().next().value as string | undefined;
      if (!oldest) break;
      this.entityTabCache.delete(oldest);
    }
  },
  restoreCachedEntity(this: WorkspaceEntityCacheStore, id: string) {
    const key = this.entityCacheKey(id);
    const cached = this.entityTabCache.get(key);
    if (!cached || cached.contextKey !== this.entityCacheContextKey()) return null;
    this.entityTabCache.delete(key); this.entityTabCache.set(key, cached);
    this.activeEntity = cached.entity;
    this.activeEntityTags = cached.activeEntityTags;
    this.characterHistory = cached.characterHistory;
    this.characterHistoryTimelineOptions = cached.characterHistoryTimelineOptions;
    this.characterHistoryLoaded = cached.characterHistoryLoaded;
    this.entityFieldHistory = cached.entityFieldHistory;
    this.resolvedEntityFieldValues = cached.resolvedEntityFieldValues;
    this.backlinks = cached.backlinks;
    this.sceneSegments = cached.sceneSegments;
    this.sceneSegmentFields = cached.sceneSegmentFields;
    this.sceneConflicts = cached.sceneConflicts;
    this.saveState = 'clean';
    return cached.entity;
  },
  async validateCachedEntity(this: WorkspaceEntityCacheStore, entity: EntityDocument, generation = entityOpenGeneration(this)) {
    try {
      const state = await api<{ token: string }>(`/api/v1/entities/${entity.id}/change-state?includeHistory=${entity.historyLoaded === true}`);
      if (generation !== entityOpenGeneration(this) || this.activeEntity !== entity) return;
      if (state.token === entity.changeToken) return;
      this.entityTabCache.delete(this.entityCacheKey(entity.id));
      if (this.saveState === 'clean') await this.openEntity(entity.id, false, true, true);
    } catch { /* Cached content stays usable while the validation check is offline. */ }
  },
  async openEntity(this: WorkspaceEntityCacheStore, id: string, updateHistory = true, force = false, requireClean = false) {
    this.cacheActiveEntity();
    invalidateEntityOpenRequests(this);
    const generation = entityOpenGeneration(this);
    if (!force) {
      const cached = this.restoreCachedEntity(id);
      if (cached) {
        if (updateHistory) history.pushState({}, '', `/w/${this.workspace?.id}/e/${id}`);
        void this.validateCachedEntity(cached, generation);
        return cached;
      }
    }
    const entity = await api<EntityDocument>(`/api/v1/entities/${id}?includeHistory=false`);
    if (generation !== entityOpenGeneration(this) || (requireClean && this.saveState !== 'clean')) return this.activeEntity;
    this.activeEntity = entity;
    const opened = this.activeEntity;
    const isScene = opened.templateCategory === 'szene';
    if (!isScene) { this.sceneSegments = []; this.sceneSegmentFields = []; this.sceneConflicts = []; }
    this.characterHistory = [];
    this.characterHistoryLoaded = false;
    this.characterHistoryTimelineOptions = { chapters: [], scenes: [], segments: [], states: [], suggestions: [] };
    await Promise.all([
      this.loadResolvedEntityFieldValues(id),
      this.loadEntityFieldHistory(id),
      this.loadEntityTags(id),
      this.loadBacklinks(id),
      ...(isScene ? [this.loadSceneSegments(id), this.loadSceneConflicts(id)] : [])
    ]);
    if (generation !== entityOpenGeneration(this) || this.activeEntity !== opened) return this.activeEntity;
    try {
      opened.changeToken = (await api<{ token: string }>(`/api/v1/entities/${id}/change-state?includeHistory=false`)).token;
    } catch { /* A missing token only disables caching for this visit. */ }
    this.saveState = 'clean';
    if (updateHistory) history.pushState({}, '', `/w/${this.workspace?.id}/e/${id}`);
    return opened;
  }
};
